<template>
    <DsAnchorElement
        :class="[
            { 'ds-button-link--chevron': hasChevron('right'), 'ds-button-link--static-icon': !moveIconOnHover },
            linkType,
            iconPadding
        ]"
        :tracking-data="{ 'component-name': $options.name, ...trackingData }"
        :url="url"
        :open-in-new-window="openInNewWindow"
        :nuxt="nuxt"
        :prefetch="prefetch"
        :aria-label="computedAriaLabel"
        role="link"
        :download="download"
        data-j-link
        @link-clicked="(e) => $emit('link-clicked', {...e, label: computedAriaLabel })">
        <!-- link with text and optional icons -->
        <DsTextIconElement
            :class="{ 'ds-heading-xsmall': heading && type === 'link' }"
            :icon-left="iconLeft ? iconLeft : null"
            :icon-right="iconRight ? iconRight : null"
            :text="!iconOnly ? content : null"
            :icon="iconOnly ? content : null"
            :with-spacing="type === 'button'"
            data-j-link-text />
    </DsAnchorElement>
</template>

<script>
import { DsAnchorElement, DsTextIconElement } from '../../atoms';
/**
 * DsAnchorElement Clicked event
 * @event link-clicked
 * @type {Object}
 * @property {String} url - link url
 * @property {String} label - link text
 */

/**
 * Link component
 *
 * @example
 * ```js
 * import DsLinkWithMedia from '@/components/link-with-media/link-with-media'
 *
 * export default {
 *   components: { DsLink },
 *   template: `<div>
 *          <!-- DsLink with text -->
 *              <DsLink
 *                  content="Link text"
 *                  :url="#",
 *                  :openInNewWindow="false",
 *                  :nuxt="false"
 *              />
*           <!-- DsLink with text and icons (all icons are optional) -->
 *              <DsLink
 *                  content="Link text"
 *                  :url="#",
 *                  :openInNewWindow="false",
 *                  :nuxt="false"
 *                  :icon-left="{ category: 'icon-category', name: 'icon-name' }"
 *                  :icon-right="{ category: 'icon-category', name: 'icon-name' }"
 *              />
 *              <!-- DsLink with icon only (no text) -->
 *              <DsLink
 *                  :content="{ category: 'icon-category', name: 'icon-name', ariaLabel: 'aria-label' }"
 *                  :url="#",
 *                  :openInNewWindow="false",
 *                  :nuxt="false"
 *              />
 *
 *              <!-- If you want the link to look like a button, pass the prop type="button" -->
*               <!-- To render the different button looks variants, you can pass type="button" and then one of the following values:
*                      variant=['primary']
*                      color=['green', 'black', 'white']
 *              -->
 *              <!-- To render the different link looks variants, you can pass type="link" and then one of the following values:
 *                      variant=['primary', 'secondary', 'tertiary']
 *                      color=['green', 'black', 'white', 'black-green', 'white-green']
 *              -->
 *              <!-- If you want the link to have a bigger text, pass the prop heading -->
 *      </div>`
 * }
 * ```
 */
export default {
    name: 'DsLink',
    components: { DsAnchorElement, DsTextIconElement },
    props: {
        /**
         * trackingData, to be set as data-track attributes for tracking purpose
         */
        trackingData: { type: Object, default: () => ({}) },
        /**
         * Link url, href attribute content
         */
        url: { type: String, required: true },
        /**
         * Link openInNewWindow, when is set to true it will open the link in a new window
         */
        openInNewWindow: { type: Boolean, default: false },
        /**
         * Link nuxt, when is set to true it will render a nuxt link (vue router link)
         */
        nuxt: { type: Boolean, default: false },
        /**
         * Link prefetch, when is set to true it will nuxt will prefetch the page
         */
        prefetch: { type: Boolean, default: false },
        /**
         * Link ariaLabel, arial-label attribute content
         */
        ariaLabel: { type: String, default: '' },
        /**
         * Link text or icon object
         */
        content: {
            type: [String, Object],
            required: true,
            validator: el => el instanceof Object ? ['name', 'category', 'ariaLabel'].every(key => key in el) : true
        },
        /**
         * Link text size (if set to true, it will set heading-xsmall font size)
         */
        heading: {
            type: Boolean, default: false
        },
        /**
         * Link left icon (optional)
         */
        iconLeft: {
            type: [Object, null],
            default: null,
            validator: obj => ['name', 'category'].every(key => key in obj)
        },
        /**
         * Link right icon (optional)
         */
        iconRight: {
            type: [Object, null],
            default: null,
            validator: obj => ['name', 'category'].every(key => key in obj)
        },
        /**
         * Link look (button or link style)
         */
        type: {
            type: String,
            default: 'link',
            validator: el => ['button', 'link'].includes(el)
        },
        /**
         * Link variant (primary, secondary, tertiary, quaternary, quinary)
         */
        variant: {
            type: String,
            default: 'primary',
            validator: el => ['primary', 'secondary', 'tertiary', 'quaternary', 'quinary'].includes(el)
        },
        /**
         * Link color (green, white, black...)
         */
        color: {
            type: String,
            default: 'green',
            validator: el => ['black', 'green', 'white', 'white-green', 'black-green'].includes(el)
        },
        /**
         * download, adds download attribute
         */
        download: { type: Boolean, default: false },
        /**
         * moveIconOnHover, if set to false the icon will not move on hover
         */
        moveIconOnHover: { type: Boolean, default: true }
    },
    computed: {
        iconOnly () {
            return this.content && this.content instanceof Object;
        },
        computedAriaLabel () {
            return (this.content && this.content instanceof Object)
                ? this.content.ariaLabel
                : this.ariaLabel || this.content;
        },
        linkType () {
            // When ds-button-link will be renamed to ds-link, this will be: return `ds-${type} ds-${type}--${variant} ds-${type}--${color}`;
            return this.type === 'button'
                ? (this.iconOnly ? `ds-button-icon ds-button-icon--${this.variant} ds-button-icon--${this.color}` : `ds-button ds-button--${this.variant} ds-button--${this.color}`)
                : `ds-button-link ds-button-link--${this.variant} ds-button-link--${this.color}`;
        },
        iconPadding () {
            return this.type === 'button' ?
                (this.iconLeft
                    ? (this.iconRight
                        ? 'ds-button--with-icon-left ds-button--with-icon-right'
                        : 'ds-button--with-icon-left')
                    : (this.iconRight
                        ? 'ds-button--with-icon-right'
                        : ''))
                : '' ;
        }
    },
    methods: {
        hasChevron (side) {
            return (side === 'right')
                ? this.iconRight && ['chevron-right-small', 'chevron-right', 'chevron-left', 'chevron-left-small'].includes(this.iconRight.name)
                : this.iconLeft && ['chevron-right-small', 'chevron-right', 'chevron-left', 'chevron-left-small'].includes(this.iconLeft.name);
        }
    }
};
</script>
